/**
 * A boolean that turns itself off after a delay, re-armed by a trigger value.
 *
 * The sibling of `useTransientValue`, for the *effect-driven* case. The
 * distinction matters and is why there are two:
 *
 * - `useTransientValue` is **imperative** — a handler calls `show(x)`. Use it
 *   when something the user did produces a message.
 * - `useTransientFlag` is **declarative** — visibility follows a prop/key that
 *   changed upstream. Use it when a parent's state change should flash
 *   something.
 *
 * Trying to serve both from one hook means either an imperative API that
 * callers must wire into an effect themselves (which is the boilerplate this
 * removes) or a `show` that fires on render.
 *
 * `triggerKey` is a bump counter or id, not the displayed content: the flag
 * re-arms when it *changes*, so repeating the same event must change the key.
 * `enabled: false` forces the flag off and skips arming — for the common
 * "below threshold, don't show at all" guard.
 */
import { useEffect, useState } from 'react';

export function useTransientFlag(
  triggerKey: unknown,
  durationMs: number,
  enabled = true
): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setVisible(false);
      return;
    }
    setVisible(true);
    const id = window.setTimeout(() => setVisible(false), durationMs);
    // Both a debounce and an unmount guard: a fast re-trigger must not leave
    // the previous timer running, and unmounting mid-window must not setState.
    return () => window.clearTimeout(id);
  }, [triggerKey, durationMs, enabled]);

  return visible;
}
